import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { cn } from '../lib/utils';

interface PageHeaderProps {
  /** Page h1 — every routed page renders exactly one. */
  title: string;
  /** One-line teaching description under the title. */
  description?: string;
  /**
   * Right-aligned page verbs: the primary action at default size, and
   * navigation links ("License Keys"). Nothing else. The way back from a page
   * kept out of the nav is `back`, never an action.
   *
   * Search, filters and the column picker never go here — they live in
   * DataTable's `filters` / `search` / `columnPicker` card-header slots.
   */
  actions?: React.ReactNode;
  /**
   * The parent of a page kept out of the nav, rendered as '← {label}' above
   * the h1. Pass it only from useParentLink, which returns it only when the
   * caller may open the parent. Needs a Router.
   */
  back?: { to: string; label: string };
  className?: string;
}

/**
 * Standard opening block for every authenticated page: h1 (focus target for
 * route changes), description, and the page-level actions.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({ title, description, actions, back, className }) => (
  <div className={cn('flex flex-wrap items-start justify-between gap-3', className)}>
    <div className="min-w-0">
      {back ? (
        // Accessible name "Back to {label}": a bare "{label}" would repeat the
        // sidebar link's name. The visible text is contained in it.
        <Link
          to={back.to}
          className="mb-1 inline-flex items-center gap-1 rounded-sm text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />
          <span className="sr-only">Back to </span>
          {back.label}
        </Link>
      ) : null}
      <h1 tabIndex={-1} className="text-2xl font-semibold tracking-tight outline-none">
        {title}
      </h1>
      {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
    </div>
    {/*
      From `sm` up the actions stay on ONE line among themselves — otherwise a
      primary button drops to a second row beneath the navigation link beside
      it. When the whole group no longer fits beside the title, the OUTER
      `flex-wrap` moves it below the title intact.

      Below `sm` they wrap, and that is deliberate rather than a concession:
      `justify-end` means an unwrappable row overflows off the LEFT edge, which
      `scrollWidth - clientWidth` cannot detect — so responsive.spec.ts would
      keep passing while the controls were clipped out of reach.
    */}
    {actions ? (
      <div className="flex min-w-0 flex-wrap items-center justify-end gap-2 sm:flex-nowrap">
        {actions}
      </div>
    ) : null}
  </div>
);
